import React, { useCallback, useEffect, useState } from 'react';
import {
  Calendar, FileText, FileSpreadsheet, Eye, Trash2, RefreshCw, Loader2,
} from 'lucide-react';
import { toast } from 'react-toastify';
import MizanService from '../../services/mizanService';
import { useCompany } from '../../contexts/CompanyContext';
import { useLanguage } from '../../contexts/LanguageContext';

const formatUploadedMizanFileName = (mizan) =>
  String(mizan?.fileName || mizan?.generatedFileName || '-');

const UploadedTrialBalanceFilesList = ({
  refreshToken = 0,
  onViewMizan,
  onDeletedMizanId,
  onListLoaded,
  hideToolbar = false,
  onToolbarRender,
}) => {
  const { companyId, taxId } = useCompany();
  const { t } = useLanguage();
  const [uploadedMizans, setUploadedMizans] = useState([]);
  const [loadingMizans, setLoadingMizans] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchUploadedMizans = useCallback(async () => {
    if (!taxId) return;
    setLoadingMizans(true);
    try {
      const result = await MizanService.getPeriodMizansByIdentifier(
        taxId,
        'TRIAL_BALANCE_UPLOAD'
      );
      if (result.success) {
        const list = result.data || [];
        list.sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
        setUploadedMizans(list);
        onListLoaded?.(list);
      } else {
        toast.error(result.error || 'Failed to load trial balance documents.');
      }
    } catch (err) {
      console.error('Fetch uploaded trial balances error:', err);
      toast.error('Failed to load trial balance documents.');
    } finally {
      setLoadingMizans(false);
    }
  }, [taxId, onListLoaded]);

  useEffect(() => {
    onToolbarRender?.({ onRefresh: fetchUploadedMizans, loading: loadingMizans });
  }, [onToolbarRender, fetchUploadedMizans, loadingMizans]);

  useEffect(() => {
    fetchUploadedMizans();
  }, [fetchUploadedMizans, refreshToken]);

  const handleDeleteMizan = async (mizanId) => {
    if (!window.confirm('Bu mizanı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.')) {
      return;
    }

    setDeletingId(mizanId);
    try {
      const isPeriodMizanId = typeof mizanId === 'number' || /^\d+$/.test(String(mizanId || ''));
      const result = isPeriodMizanId
        ? await MizanService.deletePeriodMizan(mizanId)
        : await MizanService.deleteMizan(mizanId);

      if (result.success) {
        toast.success('Mizan başarıyla silindi.');
        await fetchUploadedMizans();
        onDeletedMizanId?.(mizanId);
      } else {
        toast.error(result.error || 'Mizan silinirken bir hata oluştu.');
      }
    } catch (err) {
      console.error('Delete trial balance error:', err);
      toast.error('Mizan silinirken bir hata oluştu.');
    } finally {
      setDeletingId(null);
    }
  };

  if (!companyId) {
    return (
      <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl px-4 py-3">
        <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">{t('edefter.noCompanySelected')}</p>
      </div>
    );
  }

  const refreshButton = (
    <button
      type="button"
      onClick={fetchUploadedMizans}
      disabled={loadingMizans}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-white/10 rounded-xl hover:bg-gray-50 dark:hover:bg-white/5 transition-colors disabled:opacity-50"
    >
      {loadingMizans ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
      {t('edefter.refresh')}
    </button>
  );

  return (
    <div>
      {!hideToolbar && (
        <div className="flex items-center justify-end mb-4">
          {refreshButton}
        </div>
      )}

      {loadingMizans ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Loader2 size={32} className="text-[#24299d] animate-spin" />
          <p className="text-sm text-gray-500">{t('edefter.loading')}</p>
        </div>
      ) : uploadedMizans.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-4 border-2 border-dashed border-gray-200 dark:border-white/10 rounded-2xl">
          <FileSpreadsheet size={36} className="text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center">{t('edefter.noUploadedFiles')}</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-gray-100 dark:border-white/5 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-white/5 border-b border-gray-100 dark:border-white/5">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {t('edefter.fileName')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {t('edefter.dateRange')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {t('edefter.timestamp')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {t('edefter.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-white/5">
                {uploadedMizans.map((mizan, index) => (
                  <tr
                    key={mizan.mizanId ?? index}
                    className="hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 min-w-0">
                        {mizan.extension === 'pdf' ? (
                          <FileText size={16} className="text-red-500 shrink-0" />
                        ) : (
                          <FileSpreadsheet size={16} className="text-[#24299d] dark:text-[#818cf8] shrink-0" />
                        )}
                        <span className="text-gray-800 dark:text-gray-200 truncate">
                          {formatUploadedMizanFileName(mizan)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300">
                        <Calendar size={13} className="text-gray-400 shrink-0" />
                        {mizan.tarihAraligi || '-'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                      {mizan.timestamp || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          title={t('edefter.view')}
                          onClick={() =>
                            onViewMizan?.({
                              mizanId: mizan.mizanId,
                              fileName: formatUploadedMizanFileName(mizan),
                              donem: mizan.donem ?? null,
                              tarihAraligi: mizan.tarihAraligi ?? null,
                              periodCoveredStart: mizan.periodCoveredStart ?? mizan.periodStart ?? null,
                              periodCoveredEnd: mizan.periodCoveredEnd ?? mizan.periodEnd ?? null,
                            })
                          }
                          className="p-2 text-[#24299d] dark:text-[#818cf8] hover:bg-[#24299d]/10 rounded-lg transition-colors"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          type="button"
                          title="Delete"
                          onClick={() => handleDeleteMizan(mizan.mizanId)}
                          disabled={deletingId === mizan.mizanId}
                          className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {deletingId === mizan.mizanId ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Trash2 size={16} />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadedTrialBalanceFilesList;
